import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { lazy, Suspense, type ComponentType } from 'react';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { Spinner } from '@/components/ui/Spinner';

// A new deploy replaces the hashed chunk filenames, so a tab opened before the
// deploy will 404 when it lazy-loads a route ("Failed to fetch dynamically
// imported module"). Reload once to pick up the fresh bundle instead of showing
// an error; a sessionStorage guard (cleared on any successful load) prevents a
// reload loop if the chunk is genuinely missing.
const RELOAD_KEY = 'k8sum:chunk-reload';
function lazyWithReload<T extends { default: ComponentType<unknown> }>(factory: () => Promise<T>) {
  return lazy(() =>
    factory()
      .then((m) => {
        sessionStorage.removeItem(RELOAD_KEY);
        return m;
      })
      .catch((err) => {
        if (!sessionStorage.getItem(RELOAD_KEY)) {
          sessionStorage.setItem(RELOAD_KEY, '1');
          window.location.reload();
          return new Promise<T>(() => {}); // hang until the reload happens
        }
        throw err;
      }),
  );
}

const LoginPage = lazyWithReload(() => import('@/pages/LoginPage'));
const OidcCallbackPage = lazyWithReload(() => import('@/pages/OidcCallbackPage'));
const DashboardPage = lazyWithReload(() => import('@/pages/DashboardPage'));
const UsersListPage = lazyWithReload(() => import('@/pages/UsersListPage'));
const UserDetailPage = lazyWithReload(() => import('@/pages/UserDetailPage'));
const UserCreatePage = lazyWithReload(() => import('@/pages/UserCreatePage'));
const NamespacesPage = lazyWithReload(() => import('@/pages/NamespacesPage'));
const AuditLogPage = lazyWithReload(() => import('@/pages/AuditLogPage'));
const SettingsPage = lazyWithReload(() => import('@/pages/SettingsPage'));
const SecurityPage = lazyWithReload(() => import('@/pages/SecurityPage'));
const NotFoundPage = lazyWithReload(() => import('@/pages/NotFoundPage'));

function withSuspense(element: React.ReactNode) {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          <Spinner size={22} />
        </div>
      }
    >
      {element}
    </Suspense>
  );
}

const protect = (element: React.ReactNode) =>
  withSuspense(<ProtectedRoute>{element}</ProtectedRoute>);

const routes: RouteObject[] = [
  { path: '/', element: <Navigate to="/dashboard" replace /> },
  { path: '/login', element: withSuspense(<LoginPage />) },
  { path: '/auth/callback', element: withSuspense(<OidcCallbackPage />) },
  { path: '/dashboard', element: protect(<DashboardPage />) },
  { path: '/users', element: protect(<UsersListPage />) },
  { path: '/users/new', element: protect(<UserCreatePage />) },
  { path: '/users/:id', element: protect(<UserDetailPage />) },
  { path: '/namespaces', element: protect(<NamespacesPage />) },
  { path: '/audit', element: protect(<AuditLogPage />) },
  { path: '/settings', element: protect(<SettingsPage />) },
  { path: '/security', element: protect(<SecurityPage />) },
  { path: '*', element: withSuspense(<NotFoundPage />) },
];

export const router = createBrowserRouter(routes);
